import { type PrefixedLogger, RwcLogger } from '@ringcentral/rwc-logger';
import getConfig from './config';
import { IPC_EXTERNAL_ERROR } from './constants/ipc-action-types';
import { LOGGER_KEY } from './constants/misc';
import { getDesktopLogAPI } from './desktop-tools/desktop-api';
import { IS_DESKTOP_LOG_ENABLED } from './desktop-tools/feature-flags';
import rwcInternalApi from './internal-api';
import { readLocallyStored } from './local-storage';
import { detectSensitiveData, removeSensitiveData } from './sensitive-data';

export { labels } from '@ringcentral/rwc-logger';
export type { PrefixedLogger };

const mainConfig = getConfig('RWC_LOGGER');

/** local storage key for FFS config of RwcLogger */
export const REMOTE_RWC_LOGGER_CONF_LS_KEY = 'remoteRwcLogger';

const getFfsLoggerConfig = () => {
    try {
        // TODO: cleanup outdated key, remove this after it's in trunk
        localStorage?.removeItem('ldRwcLogger');
        return readLocallyStored(REMOTE_RWC_LOGGER_CONF_LS_KEY, {}, true);
    } catch {
        // eslint-disable-next-line no-console
        console.warn('Cannot get saved FFS rwcLogger config');
        return null;
    }
};

const addListenerToDownloadLogs = logger => {
    // Skip for WebWorker context
    if (typeof document !== 'object') return;

    document.addEventListener('keydown', evt => {
        if (evt.key?.toLowerCase() === 'l' && evt.shiftKey && evt.ctrlKey && evt.metaKey) {
            logger.downloadLogs();
        }
    });
};

/**
 * Guarantees that there is only one logger instance even when it is used by several scripts
 * @returns {RwcLogger} RwcLogger single instance
 */
const getLoggerSingleton = () => {
    if (typeof globalThis[LOGGER_KEY] === 'undefined') {
        const externalAPI = {
            desktopLog: IS_DESKTOP_LOG_ENABLED ? getDesktopLogAPI() : null,
            emit: data => rwcInternalApi.emit(IPC_EXTERNAL_ERROR, data),
            detectSensitiveData,
            removeSensitiveData,
        };
        const logger = new RwcLogger(mainConfig, externalAPI);
        globalThis[LOGGER_KEY] = logger;
        addListenerToDownloadLogs(logger);

        const ffsLoggerConfig = getFfsLoggerConfig();
        if (logger.isStorageConfigEmpty() && ffsLoggerConfig) {
            // Temporarily use config from FFS when rwcLogger is not configured manually from console.
            // This can be rolled back at runtime by using rwcLogger.reset()
            logger.applyConfig(ffsLoggerConfig, true);
        }
    }
    return globalThis[LOGGER_KEY];
};

export const rwcLogger = getLoggerSingleton();

/**
 * Creates PrefixedLogger by passed prefixes
 * @param  {...string} paths - path chunks
 * @returns {PrefixedLogger}
 */
export const getPrefixedLogger = (...paths: Array<string>): PrefixedLogger =>
    rwcLogger.byPrefix(paths.join(':'));

/**
 * Creates PrefixedLogger-like by passed prefixes whose traces lead directly to logger calls.
 * Applies logger configuration only once on a page load.
 * @param paths - path chunks
 * @returns {PrefixedLogger}
 */
export function getPrefixedConsoleLogger(...paths: string[]) {
    const prefix = '[' + paths.join(':') + ']';
    const { level, prefixes } = rwcLogger.getConfig();
    const prefixREs = prefixes.split(',').map(p => new RegExp(`${p.trim().replace('*', '.*?')}`));
    const cfg = prefixREs.some(prefixRE => prefix?.match(prefixRE))
        ? 0b1111
        : {
              off: 0b0000,
              error: 0b0001,
              warn: 0b0011,
              info: 0b0111,
              debug: 0b1111,
          }[level];
    const pass = () => void 0;
    const isTest = 'process' in globalThis;

    return {
        debug: !isTest && cfg & 0b1000 ? console.debug.bind(console, prefix) : pass,
        info: !isTest && cfg & 0b0100 ? console.log.bind(console, prefix) : pass,
        warn: cfg & 0b0010 ? console.warn.bind(console, prefix) : pass,
        error: cfg & 0b0001 ? console.error.bind(console, prefix) : pass,
    };
}

let didMountLogger;
let didUnmountLogger;
export const logMounted = (whoWasMounted: string): void => {
    if (!didMountLogger) didMountLogger = getPrefixedLogger(prefixes.VC_MOUNTED);
    didMountLogger.info(whoWasMounted);
};

export const logUnmounted = (whoWasUnmounted: string): void => {
    if (!didUnmountLogger) {
        didUnmountLogger = getPrefixedLogger(prefixes.VC_UNMOUNTED);
    }
    didUnmountLogger.info(whoWasUnmounted);
};

let locationLogger;
export const logLocation = (): void => {
    if (!locationLogger) locationLogger = getPrefixedLogger(prefixes.VC_LOCATION);
    locationLogger.info(location.href);
};

export const prefixes = {
    VC_CONTAINERS: 'vc:containers',
    VC_COMPONENTS: 'vc:components',
    VC_ACTIONS: 'vc:actions',
    VC_ANNOTATIONS: 'vc:annotations',
    VC_REDUCERS: 'vc:reducers',
    VC_SELECTORS: 'vc:selectors',
    VC_UTILS: 'vc:utils',
    VC_LOW: 'vc:low',
    VC_LOCATION: 'vc:location',
    VC_MOUNTED: 'vc:mounted',
    VC_UNMOUNTED: 'vc:unmount',
    VC_INTERNAL_API: 'vc:internal-api',
    RWC_UTILS: '@ringcentral/rwc-utils',
    LIVE_TRANSCRIPT: 'live-transcript',
    LIBSFU: 'libsfu',
};
